import { editionCoversResponseSchema, type EditionCoversResponse } from "@lifescapsule/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "../../../shared/api";
import { withStoryTitles, type CustomerBookSummary } from "../../account/customer-home-model";
import { sessionSubject } from "../../fulfillment/use-physical-order";
import { bookIdentity } from "./print-links";

/** The book a print page is about, as the reader's own Library knows it. */
export type PrintBook = {
  id: string;
  title: string;
  /** "A Bedtime Story", "The Family Cookbook"…: the kind of book, in the Library's words. */
  type: string;
  templateId?: string;
  cover: string | null;
  role: CustomerBookSummary["role"];
  /** One bounded cover-only renewal; never resets print approval or changes a signed URL. */
  onCoverError?: () => void;
};

export type PrintBookState =
  | { state: "loading" }
  | { state: "ready"; book: PrintBook }
  /** Not a book in this reader's list: nothing else is shown in its place. */
  | { state: "missing" }
  | { state: "error" };

/**
 * This book's cover alone, in the negotiated answer (as My Library asks): one book whose cover
 * can't be checked is reported as such, instead of the legacy shelf answer failing for everyone.
 */
async function bookCover(bookId: string, idToken: string) {
  return editionCoversResponseSchema.parse(
    await apiRequest<unknown>(
      `/library/covers?responseVersion=2&bookIds=${encodeURIComponent(bookId)}`,
      idToken,
    ),
  );
}

/** The cover URL the answer gives this book; `undefined` when its cover couldn't be checked. */
function coverIn(found: EditionCoversResponse, bookId: string): string | null | undefined {
  if (found.visibleBookIds && !found.visibleBookIds.includes(bookId)) return null;
  const url = found.covers.find((cover) => cover.bookId === bookId)?.url;
  if (url !== undefined) return url;
  return found.unavailableBookIds?.includes(bookId) ? undefined : null;
}

/**
 * Finds the book among the reader's own books. A book they can't see reads as missing, never as
 * another book; its cover is a nicety, so a cover that can't be read leaves just the title.
 *
 * It follows the book and the signed-in account, never the sign-in's token itself: a renewed
 * sign-in keeps the book on screen, and a form being filled in on its page (D16, 2026-11-02).
 * A link whose book id isn't one the service could have issued matches no book (D14).
 */
export function usePrintBook(bookId: string, idToken: string | undefined, attempt = 0) {
  const [value, setValue] = useState<PrintBookState>({ state: "loading" });
  const [expiresAt, setExpiresAt] = useState(0);
  const account = idToken ? sessionSubject(idToken) : "";
  const scope = `${bookId}:${account}`;
  const token = useRef(idToken);
  token.current = idToken;
  const wellFormed = Boolean(bookIdentity(bookId));
  const [valueScope, setValueScope] = useState(scope);
  const current = useRef(scope);
  current.current = scope;
  const request = useRef(0);
  const errorRenewal = useRef({ scope: "", used: false });
  const renewCover = useCallback(async () => {
    const idToken = token.current;
    if (!idToken || !bookId) return;
    const serial = ++request.current;
    try {
      const found = await bookCover(bookId, idToken);
      if (current.current !== scope || serial !== request.current) return;
      const cover = coverIn(found, bookId);
      // A cover that couldn't be checked keeps the one shown; an unchanged one keeps the same book.
      setValue((previous) =>
        previous.state === "ready" &&
        previous.book.id === bookId &&
        cover !== undefined &&
        cover !== previous.book.cover
          ? { ...previous, book: { ...previous.book, cover } }
          : previous,
      );
      setExpiresAt(Date.now() + found.expiresIn * 1000);
    } catch {
      if (current.current === scope && serial === request.current)
        setExpiresAt(Date.now() + 60_000);
    }
  }, [bookId, scope]);
  const onCoverError = useCallback(() => {
    if (errorRenewal.current.scope !== scope) errorRenewal.current = { scope, used: false };
    if (errorRenewal.current.used) return;
    errorRenewal.current.used = true;
    void renewCover();
  }, [scope, renewCover]);
  useEffect(() => {
    const idToken = token.current;
    if (!idToken || !bookId || !wellFormed) return;
    let live = true;
    ++request.current;
    setExpiresAt(0);
    setValue({ state: "loading" });
    setValueScope(scope);
    const covers = bookCover(bookId, idToken).catch(
      (): EditionCoversResponse => ({ covers: [], expiresIn: 60 }),
    );
    Promise.all([
      apiRequest<{ data: CustomerBookSummary[] }>(
        `/books?bookId=${encodeURIComponent(bookId)}`,
        idToken,
      ),
      covers,
    ])
      .then(([list, found]) => {
        if (!live) return;
        const book = withStoryTitles(list.data).find((candidate) => candidate.id === bookId);
        if (!book) {
          setValue({ state: "missing" });
          return;
        }
        setValue({
          state: "ready",
          book: {
            id: book.id,
            title: book.title,
            type: book.template.name,
            templateId: book.template.id,
            cover: coverIn(found, book.id) ?? null,
            role: book.role,
            onCoverError,
          },
        });
        setExpiresAt(Date.now() + found.expiresIn * 1000);
      })
      .catch(() => {
        if (live) setValue({ state: "error" });
      });
    return () => {
      live = false;
    };
  }, [bookId, account, attempt, onCoverError, scope, wellFormed]);
  useEffect(() => {
    if (value.state !== "ready" || !expiresAt) return;
    const refresh = () => {
      if (!document.hidden) void renewCover();
    };
    const visible = () => {
      if (!document.hidden && Date.now() >= expiresAt - 30_000) refresh();
    };
    const timer = window.setTimeout(refresh, Math.max(10_000, expiresAt - Date.now() - 30_000));
    document.addEventListener("visibilitychange", visible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [value.state, expiresAt, renewCover]);
  if (!wellFormed) return { state: "missing" as const };
  return valueScope === scope ? value : { state: "loading" as const };
}
